import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { notifications } from '@mantine/notifications'
import {
  pareItensRespostas,
  useExecucaoConcluir,
  useExecucaoDetalhe,
  useRespostaUpdate,
  useRespostasList,
  useTemplateDetalhe,
} from '../../hooks/useChecklists'
import type { RespostaItem, TemplateItem } from '../../types'
import RespostaItemEvidencias from './RespostaItemEvidencias'
import InstrumentoSelector, {
  buildObservacaoComInstrumento,
  parseObservacaoComInstrumento,
} from './InstrumentoSelector'
import AssinaturaModal from './AssinaturaModal'
import styles from '../_shared/CrudPage.module.css'

const STATUS_LABEL: Record<string, string> = {
  em_andamento: 'Em andamento',
  concluida: 'Concluída',
}

function extractErrorMsg(err: unknown): string {
  const e = err as { response?: { data?: Record<string, unknown> } }
  const data = e?.response?.data ?? {}
  for (const k of ['detail', 'status', 'execucao']) {
    const v = data[k]
    if (Array.isArray(v) && v.length) return String(v[0])
    if (typeof v === 'string') return v
  }
  return 'Erro ao executar ação.'
}

interface RespostaInputProps {
  item: TemplateItem
  resposta: RespostaItem
  ordemServicoId: string
  readonly?: boolean
}

function RespostaInput({ item, resposta, ordemServicoId, readonly }: RespostaInputProps) {
  const update = useRespostaUpdate()
  const draftKey = `checklist-draft:${resposta.id}`
  const [valor, setValor] = useState<RespostaItem>(() => {
    // Tenta restaurar rascunho local (não aplicável a execução readonly)
    if (readonly) return resposta
    try {
      const raw = localStorage.getItem(draftKey)
      if (raw) {
        const draft = JSON.parse(raw) as Partial<RespostaItem>
        // Só usa se draft é mais novo que o respondido_em do servidor
        const draftTs = (draft as { __ts?: number }).__ts ?? 0
        const serverTs = resposta.respondido_em
          ? new Date(resposta.respondido_em).getTime()
          : 0
        if (draftTs > serverTs) {
          return { ...resposta, ...draft }
        }
      }
    } catch {
      /* ignora — localStorage indisponível */
    }
    return resposta
  })

  // Sync quando server retorna novo valor (ex.: outro tab)
  useEffect(() => {
    setValor(resposta)
  }, [resposta.id, resposta.respondido_em])

  async function persist(patch: Partial<RespostaItem>): Promise<void> {
    const novoValor = { ...valor, ...patch }
    setValor(novoValor)
    // Salva rascunho local antes do POST — sobrevive a refresh/desconexão.
    try {
      localStorage.setItem(
        draftKey,
        JSON.stringify({ ...patch, __ts: Date.now() }),
      )
    } catch {
      /* ignora — localStorage cheio ou desabilitado */
    }
    try {
      await update.mutateAsync({ id: resposta.id, data: patch })
      // Sucesso: limpa rascunho local
      try {
        localStorage.removeItem(draftKey)
      } catch {
        /* ignora */
      }
    } catch (err) {
      notifications.show({
        color: 'orange',
        title: 'Salvo localmente',
        message: 'Sem conexão — sua resposta ficará no rascunho local até voltar.',
      })
      void err
    }
  }

  if (readonly) {
    return (
      <div style={{ color: '#666', fontSize: '0.9rem' }}>
        {valor.nao_aplicavel
          ? 'Não aplicável'
          : item.tipo_resposta === 'sim_nao'
            ? valor.resposta_sim_nao === true
              ? 'Sim'
              : valor.resposta_sim_nao === false
                ? 'Não'
                : '—'
            : item.tipo_resposta === 'numerico'
              ? (valor.resposta_numerica ?? '—')
              : (valor.resposta_texto ?? '—')}
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
      {item.tipo_resposta === 'sim_nao' && (
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            type="button"
            data-testid={`resp-sim-${item.id}`}
            onClick={() => persist({ resposta_sim_nao: true, nao_aplicavel: false })}
            style={{
              padding: '0.4rem 1rem',
              border: '2px solid',
              borderColor: valor.resposta_sim_nao === true && !valor.nao_aplicavel ? '#388E3C' : '#E0E0E0',
              background: valor.resposta_sim_nao === true && !valor.nao_aplicavel ? '#C8E6C9' : 'white',
              cursor: 'pointer',
              borderRadius: '4px',
              fontWeight: valor.resposta_sim_nao === true && !valor.nao_aplicavel ? 700 : 400,
            }}
          >
            Sim
          </button>
          <button
            type="button"
            data-testid={`resp-nao-${item.id}`}
            onClick={() => persist({ resposta_sim_nao: false, nao_aplicavel: false })}
            style={{
              padding: '0.4rem 1rem',
              border: '2px solid',
              borderColor: valor.resposta_sim_nao === false && !valor.nao_aplicavel ? '#C62828' : '#E0E0E0',
              background: valor.resposta_sim_nao === false && !valor.nao_aplicavel ? '#FFCDD2' : 'white',
              cursor: 'pointer',
              borderRadius: '4px',
              fontWeight: valor.resposta_sim_nao === false && !valor.nao_aplicavel ? 700 : 400,
            }}
          >
            Não
          </button>
        </div>
      )}

      {item.tipo_resposta === 'numerico' && (
        <>
          <input
            type="number"
            step="0.01"
            data-testid={`resp-num-${item.id}`}
            value={valor.resposta_numerica ?? ''}
            onChange={(e) => setValor((v) => ({ ...v, resposta_numerica: e.target.value }))}
            onBlur={() =>
              persist({
                resposta_numerica: valor.resposta_numerica || null,
                nao_aplicavel: false,
              })
            }
            placeholder={item.unidade ? `valor (${item.unidade})` : 'valor'}
            style={{ padding: '0.4rem', minWidth: '120px' }}
            disabled={valor.nao_aplicavel}
          />
          {(() => {
            const [instrId, obsLivre] = parseObservacaoComInstrumento(valor.observacao ?? '')
            return (
              <InstrumentoSelector
                value={instrId}
                onChange={(novoId) => {
                  const novaObs = buildObservacaoComInstrumento(novoId, obsLivre)
                  setValor((v) => ({ ...v, observacao: novaObs }))
                  void persist({ observacao: novaObs })
                }}
                disabled={readonly || valor.nao_aplicavel}
                testId={`resp-instr-${item.id}`}
              />
            )
          })()}
        </>
      )}

      {item.tipo_resposta === 'texto' && (
        <input
          type="text"
          data-testid={`resp-txt-${item.id}`}
          value={valor.resposta_texto ?? ''}
          onChange={(e) => setValor((v) => ({ ...v, resposta_texto: e.target.value }))}
          onBlur={() =>
            persist({ resposta_texto: valor.resposta_texto ?? '', nao_aplicavel: false })
          }
          placeholder="resposta..."
          style={{ padding: '0.4rem', minWidth: '200px', flex: 1 }}
          disabled={valor.nao_aplicavel}
        />
      )}

      <label style={{ display: 'flex', gap: '0.25rem', fontSize: '0.85rem', color: '#666' }}>
        <input
          type="checkbox"
          data-testid={`resp-na-${item.id}`}
          checked={valor.nao_aplicavel}
          onChange={(e) => persist({ nao_aplicavel: e.target.checked })}
        />
        N/A
      </label>

      <RespostaItemEvidencias
        ordemServicoId={ordemServicoId}
        respostaItemId={resposta.id}
        readonly={readonly}
      />
    </div>
  )
}

export default function ExecucaoChecklistPage() {
  const { id = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const execucao = useExecucaoDetalhe(id)
  const template = useTemplateDetalhe(execucao.data?.template ?? '')
  const respostas = useRespostasList(id)
  const concluir = useExecucaoConcluir(id)

  if (execucao.isLoading) return <div className={styles.loading}>Carregando execução...</div>
  if (execucao.error || !execucao.data) {
    return <div className={styles.empty}>Execução de checklist não encontrada.</div>
  }

  const exec = execucao.data
  const readonly = exec.status === 'concluida'
  const pares = pareItensRespostas(template.data, respostas.data?.results)
  const [mostrarAssinatura, setMostrarAssinatura] = useState(false)

  async function handleConcluir() {
    if (!confirm('Concluir execução? Itens obrigatórios e foto-obrigatória serão validados.'))
      return
    try {
      await concluir.mutateAsync()
      notifications.show({ color: 'green', message: 'Execução concluída.' })
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Falha ao concluir',
        message: extractErrorMsg(err),
      })
    }
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <button
            type="button"
            className={styles.backBtn}
            onClick={() => navigate(-1)}
          >
            ← Voltar
          </button>
          <h1 className={styles.title}>{exec.template_nome ?? 'Execução de Checklist'}</h1>
          <span
            data-testid="execucao-status"
            style={{
              display: 'inline-block',
              padding: '0.2rem 0.6rem',
              borderRadius: '4px',
              backgroundColor: exec.status === 'concluida' ? '#C8E6C9' : '#FFE0B2',
              color: exec.status === 'concluida' ? '#1B5E20' : '#E65100',
              fontSize: '0.85rem',
              marginTop: '0.5rem',
            }}
          >
            {STATUS_LABEL[exec.status] ?? exec.status}
          </span>
        </div>
        {!readonly && (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              type="button"
              data-testid="btn-assinar-execucao"
              onClick={() => setMostrarAssinatura(true)}
              disabled={concluir.isPending}
              style={{
                padding: '0.5rem 1rem',
                background: 'white',
                color: '#1565C0',
                border: '2px solid #1565C0',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              ✍ Assinar
            </button>
            <button
              type="button"
              className={styles.btnPrimary}
              data-testid="btn-concluir-execucao"
              onClick={handleConcluir}
              disabled={concluir.isPending || respostas.isLoading || template.isLoading}
            >
              {concluir.isPending ? 'Concluindo...' : '✓ Concluir checklist'}
            </button>
          </div>
        )}
      </header>

      <AssinaturaModal
        open={mostrarAssinatura}
        onClose={() => setMostrarAssinatura(false)}
        ordemServicoId={exec.ordem_servico}
        contexto={`Conclusão checklist — ${exec.template_nome ?? id.slice(0, 8)}`}
        onAssinado={() => {
          notifications.show({
            color: 'green',
            message: 'Assinatura registrada como evidência (laudo).',
          })
        }}
      />

      {(template.isLoading || respostas.isLoading) && (
        <p className={styles.loading}>Carregando itens...</p>
      )}

      <div data-testid="checklist-itens" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {pares.map(({ item, resposta, visivel }) =>
          !visivel ? null : (
          <div
            key={item.id}
            data-testid={`checklist-item-${item.ordem}`}
            style={{
              background: 'white',
              padding: '1rem',
              borderRadius: '6px',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              borderLeft: item.depende_de ? '3px solid #1565C0' : 'none',
              marginLeft: item.depende_de ? '1rem' : '0',
            }}
          >
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
              <span style={{ fontWeight: 600, color: '#1565C0' }}>{item.ordem}.</span>
              <div style={{ flex: 1 }}>
                <p style={{ margin: 0, fontWeight: 500 }}>
                  {item.pergunta}
                  {item.obrigatorio && (
                    <span style={{ color: '#C62828', marginLeft: '0.25rem' }}>*</span>
                  )}
                  {item.requer_evidencia && (
                    <span
                      title="Item exige evidência (foto/anexo)"
                      style={{
                        marginLeft: '0.5rem',
                        fontSize: '0.75rem',
                        padding: '0.1rem 0.4rem',
                        background: '#FFF3E0',
                        color: '#E65100',
                        borderRadius: '999px',
                      }}
                    >
                      📷 evidência obrigatória
                    </span>
                  )}
                </p>
                {item.ajuda && (
                  <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem', color: '#666' }}>
                    {item.ajuda}
                  </p>
                )}
                <div style={{ marginTop: '0.5rem' }}>
                  <RespostaInput
                    item={item}
                    resposta={resposta}
                    ordemServicoId={exec.ordem_servico}
                    readonly={readonly}
                  />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>


      {pares.length === 0 && !template.isLoading && !respostas.isLoading && (
        <p className={styles.empty}>Nenhum item para responder.</p>
      )}
    </div>
  )
}
